/**
 * BadgeProgress Component
 *
 * A compact card showing progress toward the next badge with a mini progress bar.
 * Displayed on the report submission screen to encourage engagement.
 */
import React from 'react';
import { View, Image, StyleSheet } from 'react-native';
import CustomText from './CustomText';
import AppStyles from '../AppStyles';
import { getBadgeImage, getBadgePlaceholder } from '../util/badgeUtils';
import { useTranslation } from '../i18n';

/**
 * Mini progress bar component
 */
const MiniProgressBar = ({ current, total, badgeName = null }) => {
  const { t } = useTranslation('badges');
  const progress = Math.min(current / total, 1);
  const targetName = badgeName || t('nextBadgeFallback');
  return (
    <View
      style={progressBarStyles.track}
      accessibilityRole="progressbar"
      accessibilityValue={{
        min: 0,
        max: total,
        now: current,
      }}
      accessibilityLabel={t('progressTowardLabel', { badge: targetName, current, total })}
    >
      <View style={[progressBarStyles.fill, { width: `${progress * 100}%` }]} />
    </View>
  );
};

const progressBarStyles = StyleSheet.create({
  track: {
    flex: 1,
    height: 6,
    backgroundColor: '#F0F0F0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    backgroundColor: '#D4A84B',
    borderRadius: 3,
  },
});

/**
 * BadgeProgress Component
 *
 * @param {Object} props
 * @param {Array} props.badges - Array of earned badges (with threshold info)
 * @param {Object} props.nextBadge - Next badge to earn { name, reportsUntilEarned }
 * @param {Object} props.streak - Streak info { current, longest }
 * @param {boolean} props.loading - Whether data is loading
 * @param {Object} props.nextBadgeData - Full data for next badge (including threshold)
 * @param {string} props.organization - Organization name to display
 * @param {number} props.width - Optional fixed width to match other elements
 * @param {number} props.totalBadges - Total number of badges available (default 5)
 */
const BadgeProgress = ({
 badges = [], nextBadge = null, streak = null, loading = false, nextBadgeData = null, organization = null, width = null, totalBadges = 5 }) => {
  const { t } = useTranslation('badges');
  // Don't show anything while loading
  if (loading) {
    return null;
  }

  // Check if streak should be shown
  const showStreak = streak && streak.current >= 1;

  // Calculate progress
  const getProgress = () => {
    if (!nextBadge || !nextBadgeData) return null;
    const total = nextBadgeData.threshold || 1;
    const current = total - nextBadge.reportsUntilEarned;
    return { current, total };
  };

  const progress = getProgress();
  const nextBadgeImage = nextBadgeData ? getBadgeImage(nextBadgeData.id) : getBadgePlaceholder();

  // If no badges earned yet, show encouraging message for first badge
  if (badges.length === 0 && nextBadge) {
    return (
      <View style={[styles.container, width && { width, alignSelf: 'center' }]} accessibilityRole="text">
        {organization && (
          <CustomText font="medium" size={11} color={AppStyles.colorSet.textMuted} style={styles.orgLabel}>
            {organization}
          </CustomText>
        )}
        <View style={styles.card}>
          <View style={styles.mainContent}>
            <Image source={nextBadgeImage} style={styles.badgeImage} />
            <View style={styles.textContent}>
              <CustomText font="medium" size={13} color={AppStyles.colorSet.textPrimary}>
                {nextBadge.reportsUntilEarned === 1
                  ? t('almostThere')
                  : t('reportsToGo', { count: nextBadge.reportsUntilEarned })}
              </CustomText>
              <CustomText font="regular" size={11} color={AppStyles.colorSet.textMuted} numberOfLines={1}>
                {t('earnFirstBadge', { badge: nextBadge.name })}
              </CustomText>
              {progress && (
                <View style={styles.progressRow}>
                  <MiniProgressBar current={progress.current} total={progress.total} badgeName={nextBadge.name} />
                  <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted} style={styles.progressLabel}>
                    {progress.current}/{progress.total}
                  </CustomText>
                </View>
              )}
            </View>
          </View>
          {showStreak && (
            <View style={styles.streakBadge}>
              <CustomText font="semiBold" size={11} color="#F97316">
                {'\uD83D\uDD25'} {streak.current}w
              </CustomText>
            </View>
          )}
        </View>
      </View>
    );
  }

  // User has badges - show last earned + progress to next
  if (badges.length === 0) return null;

  const lastBadge = badges[badges.length - 1];
  const lastBadgeImage = getBadgeImage(lastBadge.id);
  const allEarned = !nextBadge;

  // Build accessibility label
  const streakText = showStreak ? ` ${t('weekStreakLabel', { count: streak.current })}` : '';
  const accessibilityText = nextBadge
    ? `${t('progressSummaryLabel', { earned: lastBadge.name, count: nextBadge.reportsUntilEarned, next: nextBadge.name })}${streakText}`
    : `${t('allBadgesEarnedLabel', { count: badges.length })}${streakText}`;

  return (
    <View style={[styles.container, width && { width, alignSelf: 'center' }]} accessibilityRole="text" accessibilityLabel={accessibilityText}>
      {organization && (
        <CustomText font="medium" size={11} color={AppStyles.colorSet.textMuted} style={styles.orgLabel}>
          {organization}
        </CustomText>
      )}
      <View style={styles.card}>
        <View style={styles.mainContent}>
          {/* Show next badge image (or last if all earned) */}
          <Image
            source={allEarned ? lastBadgeImage : nextBadgeImage}
            style={[styles.badgeImage, !allEarned && styles.badgeImageMuted]}
          />
          <View style={styles.textContent}>
            {allEarned ? (
              <>
                <CustomText font="semiBold" size={13} color={AppStyles.colorSet.success}>
                  {t('allEarnedShort')}
                </CustomText>
                <CustomText font="regular" size={11} color={AppStyles.colorSet.textMuted}>
                  {t('thankYou')}
                </CustomText>
              </>
            ) : (
              <>
                <CustomText font="medium" size={13} color={AppStyles.colorSet.textPrimary}>
                  {t('moreToNextBadge', {
                    count: nextBadge.reportsUntilEarned,
                    badge: nextBadge.name,
                  })}
                </CustomText>
                {progress && (
                  <View style={styles.progressRow}>
                    <MiniProgressBar current={progress.current} total={progress.total} badgeName={nextBadge.name} />
                    <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted} style={styles.progressLabel}>
                      {progress.current}/{progress.total}
                    </CustomText>
                  </View>
                )}
              </>
            )}
          </View>
        </View>

        {/* Streak indicator */}
        {showStreak && (
          <View style={styles.streakBadge}>
            <CustomText font="semiBold" size={11} color="#F97316">
              {'\uD83D\uDD25'} {streak.current}w
            </CustomText>
          </View>
        )}

        {/* Badge count indicator */}
        {badges.length > 0 && (
          <View style={styles.badgeCount}>
            <CustomText font="semiBold" size={10} color={AppStyles.colorSet.textMuted}>
              {badges.length}/{totalBadges}
            </CustomText>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    marginBottom: 16,
  },
  orgLabel: {
    textAlign: 'center',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: '#F0F0F0',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgeImage: {
    width: 44,
    height: 44,
    resizeMode: 'contain',
    marginRight: 12,
  },
  badgeImageMuted: {
    opacity: 0.85,
  },
  textContent: {
    flex: 1,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    gap: 8,
  },
  progressLabel: {
    minWidth: 32,
  },
  streakBadge: {
    backgroundColor: 'rgba(249, 115, 22, 0.1)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginLeft: 8,
  },
  badgeCount: {
    backgroundColor: '#F0F0F0',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    marginLeft: 6,
  },
});

export default BadgeProgress;
